import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { opportunityApi, userApi } from '../lib/api';
import {
  Search, Plus, MapPin, Calendar, DollarSign, Loader2,
  Briefcase, Mic, Camera, Star, Filter, X
} from 'lucide-react';
import { format } from 'date-fns';

const Opportunities = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [opportunities, setOpportunities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    type: searchParams.get('type') || '',
    location: searchParams.get('location') || ''
  });

  const [newOpp, setNewOpp] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    compensation: '',
    deadline: '',
    opportunity_type: 'gig'
  });

  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [oppsRes, catsRes] = await Promise.all([
        opportunityApi.getAll({
          search: searchParams.get('search') || undefined,
          category: searchParams.get('category') || undefined,
          opportunity_type: searchParams.get('type') || undefined,
          location: searchParams.get('location') || undefined
        }),
        userApi.getCategories()
      ]);
      setOpportunities(oppsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Failed to load opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.type) params.set('type', filters.type);
    if (filters.location) params.set('location', filters.location);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', type: '', location: '' });
    setSearchParams(new URLSearchParams());
  };

  const handleCreate = async () => {
    if (!newOpp.title || !newOpp.description || !newOpp.category || !newOpp.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreateLoading(true);
    try {
      await opportunityApi.create(newOpp);
      toast.success('Opportunity posted!');
      setCreateOpen(false);
      setNewOpp({
        title: '',
        description: '',
        category: '',
        location: '',
        compensation: '',
        deadline: '',
        opportunity_type: 'gig'
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create opportunity');
    } finally {
      setCreateLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'audition': return <Mic className="h-4 w-4" />;
      case 'gig': return <Briefcase className="h-4 w-4" />;
      case 'casting': return <Camera className="h-4 w-4" />;
      case 'show': return <Star className="h-4 w-4" />;
      default: return <Briefcase className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'audition': return 'opp-type-audition';
      case 'gig': return 'opp-type-gig';
      case 'casting': return 'opp-type-casting';
      case 'show': return 'opp-type-show';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="opportunities-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Opportunities
            </h1>
            <p className="text-muted-foreground">
              Find gigs, auditions, castings, and shows
            </p>
          </div>

          {isAuthenticated && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full btn-glow" data-testid="post-opportunity-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Post Opportunity
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
                    Post New Opportunity
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      placeholder="e.g., DJ needed for wedding"
                      value={newOpp.title}
                      onChange={(e) => setNewOpp({ ...newOpp, title: e.target.value })}
                      className="rounded-xl"
                      data-testid="opp-title-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select
                        value={newOpp.opportunity_type}
                        onValueChange={(val) => setNewOpp({ ...newOpp, opportunity_type: val })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="opp-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gig">Gig</SelectItem>
                          <SelectItem value="audition">Audition</SelectItem>
                          <SelectItem value="casting">Casting</SelectItem>
                          <SelectItem value="show">Show</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select
                        value={newOpp.category}
                        onValueChange={(val) => setNewOpp({ ...newOpp, category: val })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="opp-category-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Input
                      placeholder="City, State"
                      value={newOpp.location}
                      onChange={(e) => setNewOpp({ ...newOpp, location: e.target.value })}
                      className="rounded-xl"
                      data-testid="opp-location-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Compensation</Label>
                      <Input
                        placeholder="e.g., $200/hr"
                        value={newOpp.compensation}
                        onChange={(e) => setNewOpp({ ...newOpp, compensation: e.target.value })}
                        className="rounded-xl"
                        data-testid="opp-compensation-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Deadline</Label>
                      <Input
                        type="date"
                        value={newOpp.deadline}
                        onChange={(e) => setNewOpp({ ...newOpp, deadline: e.target.value })}
                        className="rounded-xl"
                        data-testid="opp-deadline-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Describe the opportunity..."
                      value={newOpp.description}
                      onChange={(e) => setNewOpp({ ...newOpp, description: e.target.value })}
                      className="rounded-xl min-h-[100px]"
                      data-testid="opp-description-input"
                    />
                  </div>

                  <Button
                    className="w-full rounded-xl btn-glow"
                    onClick={handleCreate}
                    disabled={createLoading}
                    data-testid="submit-opportunity-btn"
                  >
                    {createLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Post Opportunity
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search opportunities..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-12 h-12 rounded-xl"
                data-testid="opp-search-input"
              />
            </div>
            <Button type="submit" className="h-12 px-6 rounded-xl" data-testid="opp-search-btn">
              Search
            </Button>
          </form>

          <Button
            variant="outline"
            className="h-12 rounded-xl"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="opp-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="glass-panel rounded-2xl p-6 mb-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Type</label>
                <Select
                  value={filters.type}
                  onValueChange={(value) => setFilters({ ...filters, type: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Types</SelectItem>
                    <SelectItem value="gig">Gig</SelectItem>
                    <SelectItem value="audition">Audition</SelectItem>
                    <SelectItem value="casting">Casting</SelectItem>
                    <SelectItem value="show">Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select
                  value={filters.category}
                  onValueChange={(value) => setFilters({ ...filters, category: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Location</label>
                <Input
                  placeholder="City or region"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={applyFilters} className="rounded-xl">
                Apply Filters
              </Button>
              <Button variant="ghost" onClick={clearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </div>
        )}

        {/* Opportunities List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : opportunities.length > 0 ? (
          <div className="space-y-4">
            {opportunities.map(opp => (
              <Card key={opp.opportunity_id} className={`overflow-hidden ${getTypeColor(opp.opportunity_type)}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="secondary" className="category-badge flex items-center gap-1">
                          {getTypeIcon(opp.opportunity_type)}
                          {opp.opportunity_type}
                        </Badge>
                        <Badge variant="outline">{opp.category}</Badge>
                      </div>

                      <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                        {opp.title}
                      </h3>

                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {opp.description}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {opp.location}
                        </span>
                        {opp.compensation && (
                          <span className="flex items-center gap-1 price-tag">
                            <DollarSign className="h-4 w-4" />
                            {opp.compensation}
                          </span>
                        )}
                        {opp.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Deadline: {format(new Date(opp.deadline), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mt-3">
                        Posted by {opp.poster_name} • {format(new Date(opp.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>

                    <Button className="rounded-full" data-testid={`apply-${opp.opportunity_id}`}>
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg mb-4">No opportunities found</p>
            {isAuthenticated && (
              <Button onClick={() => setCreateOpen(true)} className="rounded-full">
                <Plus className="h-4 w-4 mr-2" />
                Post the First One
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Opportunities;
